// Path: apps/0001-estata.com/frontend/src/components/OAuthConsent.tsx
// v1.0.0 - Zustimmung, wenn eine KI-App (Claude, ChatGPT, ... ueber MCP) sich mit dem Estata-Konto verbinden will
//          (Inhaber 18.10.2026: "Wir machen ein schickes Login-Modal fuer den MCP-Zugang"; Migration 268,
//          docs/timetracking-mcp-concept.md). Login.tsx zeigt sie bei /login?oauth=<Anfrage> - mit Sitzung sofort,
//          sonst nach der Anmeldung. Ein Glas-Blatt faehrt ueber dem Login-Bild hoch: App-Symbol -> ESTATA-Symbol,
//          "Claude moechte auf dein ESTATA-Konto zugreifen", die Adresse der App (gegen Nachahmer), das Konto,
//          bei mehreren Firmen die Wahl, die Rechte, Erlauben / Ablehnen. Danach zurueck zur App (redirect_url).
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import axios from 'axios';
import { Building2, Check, Clock, Loader2, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { useLanguage } from '../lang/index.lang.tsx';

interface ConsentInfo {
  client_name: string | null;
  client_host: string | null;
  scopes: { id: string; label: string }[];
  firms: { id: string; name: string }[];
  default_tenant_id: string | null;
  person: { name: string; email: string };
}

const API = '/system/api/v1/oauth';

const SHEET: CSSProperties = {
  backgroundColor: 'rgba(28, 28, 30, 0.72)',
  backdropFilter: 'blur(28px) saturate(170%)',
  WebkitBackdropFilter: 'blur(28px) saturate(170%)',
};

const SCOPE_ICONS: Record<string, React.ElementType> = { time: Clock };

function fill(text: string, values: Record<string, string>): string {
  return Object.entries(values).reduce((out, [k, v]) => out.split(`{${k}}`).join(v), text);
}

export default function OAuthConsent({ requestId, onSignOut }: { requestId: string; onSignOut: () => void }) {
  const { t } = useLanguage();
  const [info, setInfo] = useState<ConsentInfo | null>(null);
  const [failed, setFailed] = useState('');
  const [tenant, setTenant] = useState<string | null>(null);
  const [busy, setBusy] = useState<'' | 'approve' | 'deny'>('');
  const [done, setDone] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    axios.get<ConsentInfo>(`${API}/requests/${encodeURIComponent(requestId)}`, { withCredentials: true })
      .then((r) => { setInfo(r.data); setTenant(r.data.default_tenant_id); })
      .catch((e) => setFailed(e?.response?.status === 404 ? t('oauth.expired') : t('oauth.failed')));
  }, [requestId]);
  // Das Blatt faehrt hoch, sobald es etwas zu zeigen gibt
  useEffect(() => { if (info || failed) requestAnimationFrame(() => setShown(true)); }, [info, failed]);

  // Recht in der Sprache der Person; unbekannte (neuere Server) mit dem deutschen Text vom Server
  const scopeLabel = (s: { id: string; label: string }) => {
    const key = `oauth.scope_${s.id}`;
    const text = t(key);
    return text && text !== `[${key}]` ? text : s.label;
  };
  const app = info?.client_name || info?.client_host || t('oauth.unknownApp');

  const decide = async (approve: boolean) => {
    if (busy) return;
    setBusy(approve ? 'approve' : 'deny');
    try {
      const r = await axios.post<{ redirect_url: string }>(
        `${API}/requests/${encodeURIComponent(requestId)}/${approve ? 'approve' : 'deny'}`,
        approve ? { tenant_id: tenant } : {}, { withCredentials: true });
      if (approve) setDone(true);
      window.setTimeout(() => window.location.assign(r.data.redirect_url), approve ? 900 : 0);
    } catch (e: any) {
      setBusy('');
      setFailed(e?.response?.status === 400 ? t('oauth.expired') : t('oauth.failed'));
    }
  };

  const signOut = async () => {
    try { await axios.post('/system/api/v1/auth/logout', {}, { withCredentials: true }); } catch { /* egal */ }
    onSignOut();
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-black" data-oauth-consent="">
      <div className="absolute inset-0 bg-cover bg-center login-drift"
           style={{ backgroundImage: "url('/images/EST-1304673834.jpg')" }} />
      <div className="absolute inset-0 bg-black/35" />

      <div className="relative z-10 min-h-screen flex flex-col justify-end sm:justify-center items-center
                      px-3 sm:px-5 pb-[calc(12px+env(safe-area-inset-bottom))] pt-[calc(24px+env(safe-area-inset-top))]">
        <div style={{ ...SHEET, transform: shown ? 'translateY(0)' : 'translateY(110%)', opacity: shown ? 1 : 0,
                      transition: 'transform 420ms cubic-bezier(0.215, 0.61, 0.355, 1), opacity 240ms ease-out' }}
             className="w-full max-w-md rounded-[32px] border border-white/10 text-white shadow-2xl px-6 pt-7 pb-6">
          {!info && !failed && (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-white/80" /></div>
          )}

          {failed && !info && (
            <div className="text-center py-6" data-oauth-failed="">
              <Lock className="w-9 h-9 mx-auto mb-4 text-white/70" />
              <p className="text-[17px] leading-snug">{failed}</p>
            </div>
          )}

          {info && (
            <>
              {/* App -> ESTATA */}
              <div className="flex items-center justify-center gap-3 mb-6" aria-hidden="true">
                <span className="w-16 h-16 rounded-[18px] bg-gradient-to-br from-[#5e5ce6] to-[#bf5af2] flex items-center justify-center
                                 text-[26px] font-semibold shadow-lg">
                  {info.client_name ? info.client_name.trim().charAt(0).toUpperCase() : <Sparkles className="w-7 h-7" />}
                </span>
                <span className="flex gap-1.5">
                  {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/50" />)}
                </span>
                <img src="/myest/icon-192.png" alt="" className="w-16 h-16 rounded-[18px] shadow-lg" />
              </div>

              <h1 className="text-center text-[24px] leading-[1.2] font-semibold tracking-[-0.01em] mb-2" data-oauth-title="">
                {fill(t('oauth.title'), { app })}
              </h1>
              {info.client_host && (
                <p className="flex items-center justify-center gap-1.5 text-[15px] text-white/60 mb-6">
                  <Lock className="w-3.5 h-3.5" /> {info.client_host}
                </p>
              )}

              {/* Konto */}
              <div className="rounded-2xl bg-white/[0.07] px-4 py-3 mb-3 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-[17px] font-semibold">
                  {(info.person.name || info.person.email || '?').trim().charAt(0).toUpperCase()}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate text-[16px] font-medium">{info.person.name}</span>
                  <span className="block truncate text-[14px] text-white/60">{info.person.email}</span>
                </span>
                <button type="button" onClick={signOut} className="text-[15px] text-[#4f8cff] shrink-0">
                  {t('oauth.notYou')}
                </button>
              </div>

              {/* Firma */}
              {info.firms.length > 1 && (
                <div className="rounded-2xl bg-white/[0.07] mb-3 overflow-hidden" data-oauth-firms="">
                  <p className="px-4 pt-3 pb-1 text-[13px] uppercase tracking-wide text-white/50">{t('oauth.firm')}</p>
                  {info.firms.map((f) => (
                    <button key={f.id} type="button" data-firm={f.id} onClick={() => setTenant(f.id)}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left border-t border-white/[0.06] first:border-t-0">
                      <Building2 className="w-5 h-5 text-white/60 shrink-0" />
                      <span className="flex-1 truncate text-[16px]">{f.name}</span>
                      {tenant === f.id && <Check className="w-5 h-5 text-[#4f8cff]" />}
                    </button>
                  ))}
                </div>
              )}
              {info.firms.length === 0 && (
                <p className="text-[15px] text-[#ff6961] mb-3">{t('oauth.noFirm')}</p>
              )}

              {/* Rechte */}
              <div className="rounded-2xl bg-white/[0.07] px-4 py-3 mb-5">
                <p className="text-[13px] uppercase tracking-wide text-white/50 mb-2">{fill(t('oauth.mayDo'), { app })}</p>
                {info.scopes.map((s) => {
                  const Icon = SCOPE_ICONS[s.id] || ShieldCheck;
                  return (
                    <div key={s.id} className="flex items-center gap-3 py-1.5" data-scope={s.id}>
                      <span className="w-8 h-8 rounded-full bg-[#ff9f0a] flex items-center justify-center shrink-0">
                        <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                      </span>
                      <span className="text-[16px]">{scopeLabel(s)}</span>
                    </div>
                  );
                })}
                <div className="flex items-center gap-3 py-1.5">
                  <span className="w-8 h-8 rounded-full bg-[#30d158] flex items-center justify-center shrink-0">
                    <ShieldCheck style={{ width: 18, height: 18 }} />
                  </span>
                  <span className="text-[15px] text-white/75">{t('oauth.ownRights')}</span>
                </div>
              </div>

              <div className="space-y-3">
                <button type="button" data-oauth-approve="" disabled={!!busy || !tenant || done} onClick={() => decide(true)}
                        className="w-full h-14 rounded-full bg-white text-black text-[17px] font-semibold flex items-center justify-center gap-2
                                   disabled:bg-[#3a3a3c] disabled:text-[#8e8e93] transition-colors">
                  {done ? <><Check className="w-5 h-5" /> {t('oauth.connected')}</>
                    : busy === 'approve' ? <Loader2 className="w-5 h-5 animate-spin" /> : t('oauth.allow')}
                </button>
                <button type="button" data-oauth-deny="" disabled={!!busy || done} onClick={() => decide(false)}
                        className="w-full h-14 rounded-full bg-white/[0.08] text-white text-[17px] font-semibold disabled:opacity-50">
                  {busy === 'deny' ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : t('oauth.deny')}
                </button>
              </div>
              {failed && <p className="mt-3 text-center text-[15px] text-[#ff6961]">{failed}</p>}
              <p className="mt-4 text-center text-[13px] leading-snug text-white/50">{t('oauth.footnote')}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
